'use client';
import React, { useState } from 'react';

import AppLogo from '@/components/ui/AppLogo';
import LoginForm from './LoginForm';
import RegisterForm from './RegisterForm';
import ForgotPasswordForm from './ForgotPasswordForm';
import { Download, Users, Zap } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';



type AuthView = 'login' | 'register' | 'forgot';

export default function AuthScreen() {
  const [view, setView] = useState<AuthView>('login');

  return (
    <div className="min-h-screen flex">
      {/* Left panel — brand */}
      <div className="hidden lg:flex lg:w-1/2 bg-foreground flex-col justify-between p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-20 left-20 w-64 h-64 rounded-full bg-primary blur-3xl" />
          <div className="absolute bottom-20 right-20 w-48 h-48 rounded-full bg-primary blur-2xl" />
        </div>

        <div className="relative z-10">
          <div className="flex items-center gap-2 mb-16">
            <AppLogo size={36} />
            <span className="font-black text-xl text-white tracking-tight">InstaExtract</span>
          </div>

          <h1 className="text-4xl font-black text-white leading-tight mb-4 tracking-tight">
            Export Instagram Comments
            <span className="block text-primary">in Minutes</span>
          </h1>
          <p className="text-white/60 text-lg leading-relaxed max-w-sm font-medium">
            Submit a URL. We extract the comments. You receive the file.
          </p>
        </div>

        <div className="relative z-10 space-y-4">
          {[
            { icon: Download, stat: '2,400+', label: 'exports delivered' },
            { icon: Users, stat: '840+', label: 'active users' },
            { icon: Zap, stat: '2–4h', label: 'avg. turnaround' },
          ].map((item) => {
            const Icon = item.icon;
            return (
              <div key={`brand-stat-${item.label}`} className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-md bg-primary flex items-center justify-center">
                  <Icon size={18} className="text-white" />
                </div>
                <div>
                  <p className="text-white font-black tabular-nums">{item.stat}</p>
                  <p className="text-white/50 text-sm">{item.label}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Right panel — form */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 bg-background">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-2 mb-8 justify-center">
            <AppLogo size={32} />
            <span className="font-black text-lg text-foreground">InstaExtract</span>
          </div>

          {view === 'login' && (
            <LoginForm
              onForgotPassword={() => setView('forgot')}
              onRegister={() => setView('register')}
            />
          )}
          {view === 'register' && (
            <RegisterForm onLogin={() => setView('login')} />
          )}
          {view === 'forgot' && (
            <ForgotPasswordForm onBack={() => setView('login')} />
          )}
        </div>
      </div>
    </div>
  );
}